<template>
  <main class="overflow-hidden">
    <section
      :class="[
        'mt-14',
        'sm:mt-0',
        'text-center',
        'mb-8',
        'sm:mb-16',
        ...gutters
      ]"
    >
      <h1
        class="font-bold text-4xl sm:text-6xl sm:mt-2 md:mt-0 bg-clip-text text-transparent bg-gradient-to-tr to-green-400 from-blue-500"
        style="line-height: normal"
      >
        Pricing
      </h1>
      <p
        class="text-normal sm:text-lg font-light text-white text-opacity-75 max-w-xl mx-auto mt-2 mb-6 sm:mt-6 sm:mb-12"
      >
        From bedroom producers to Grammy winners, we're here to make
        collaboration a breeze.
      </p>
      <div class="flex content-center justify-center">
        <button
          type="button"
          :class="
            annualPricing ? 'text-transparent bg-samplyBlue' : 'bg-gray-400'
          "
          class="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-0 focus:ring-offset-2"
          @click="annualPricing = !annualPricing"
        >
          <!-- Enabled: "translate-x-5", Not Enabled: "translate-x-0" -->
          <span
            :class="annualPricing ? 'translate-x-5' : 'translate-x-0'"
            class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
          ></span>
        </button>
        <span class="ml-3">
          <span class="text-sm font-medium text-gray-300">Annual billing </span>
          <span class="text-sm text-gray-300">(Save 20%)</span>
        </span>
      </div>
    </section>
    <!-- Mobile view -->
    <section
      :class="[
        'sm:hidden',
        'mt-14',
        'sm:mt-0',
        'mb-8',
        'sm:mb-16',
        'px-0',
        'sm:px-0',
        'lg:max-w-5xl',
        'xl:max-w-6xl'
      ]"
    >
      <figure class="rounded-lg py-8 sm:py-14 sm:px-6 lg:px-8">
        <table class="w-full h-px table-fixed">
          <!-- Table Header -->
          <thead>
            <tr>
              <th
                class="pb-4 px-6 text-sm font-medium text-gray-300 text-left"
                scope="col"
              >
                <span class="sr-only">Feature by</span>
                <span>Plan</span>
              </th>

              <th
                class="pb-4 px-6 text-lg leading-6 font-normal text-gray-300 text-left"
                scope="col"
              >
                Basic
              </th>
            </tr>
          </thead>
          <tbody class="border-t border-gray-600">
            <tr>
              <td
                class="py-8 px-6 text-sm font-normal text-gray-400 text-left align-top"
                scope="row"
              >
                Price
              </td>

              <td class="h-full py-8 px-6 align-top">
                <div class="relative h-full table">
                  <p>
                    <span class="text-4xl font-extrabold text-gray-300"
                      >$0</span
                    >
                    <span class="text-base font-medium text-gray-400">/mo</span>
                  </p>
                  <p class="mt-4 mb-4 text-sm text-gray-400">
                    Tossing a couple tracks around? No need to pay!
                  </p>
                </div>
              </td>
            </tr>
            <tr v-for="feature of featureSetQuotas" :key="feature.name">
              <td
                class="py-2 px-6 text-sm font-normal text-gray-400 text-left truncate"
                scope="row"
              >
                {{ feature.name }}
              </td>

              <td class="py-2 px-6">
                <span class="block text-sm text-gray-500">{{
                  feature.basic
                }}</span>
              </td>
            </tr>
            <tr v-for="feature of featureSetFlags" :key="feature.name">
              <td
                class="py-2 px-6 text-sm font-normal text-gray-400 text-left truncate"
                scope="row"
              >
                {{ feature.name }}
              </td>

              <td class="py-2 px-6">
                <span class="block text-sm text-gray-500">
                  <Check :value="feature.basic" />
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </figure>
      <figure class="rounded-lg py-8 sm:py-14 sm:px-6 lg:px-8">
        <table class="w-full h-px table-fixed">
          <!-- Table Header -->
          <thead>
            <tr>
              <th
                class="pb-4 px-6 text-sm font-medium text-gray-300 text-left"
                scope="col"
              >
                <span class="sr-only">Feature by</span>
                <span>Plan</span>
              </th>

              <th
                class="pb-4 px-6 text-lg leading-6 font-normal text-gray-300 text-left"
                scope="col"
              >
                Indie
              </th>
            </tr>
          </thead>
          <tbody class="border-t border-gray-600">
            <tr>
              <td
                class="py-8 px-6 text-sm font-normal text-gray-400 text-left align-top"
                scope="row"
              >
                Price
              </td>

              <td class="h-full py-8 px-6 align-top">
                <div class="relative h-full table">
                  <p>
                    <span class="text-4xl font-extrabold text-gray-300"
                      >${{
                        annualPricing
                          ? prices.indie.year.toFixed(2)
                          : prices.indie.month.toFixed(2)
                      }}</span
                    >
                    <span class="text-base font-medium text-gray-400">/mo</span>
                  </p>
                  <p class="mt-4 mb-4 text-sm text-gray-400">
                    A little more power behind your workflow.
                  </p>
                </div>
              </td>
            </tr>
            <tr v-for="feature of featureSetQuotas" :key="feature.name">
              <td
                class="py-2 px-6 text-sm font-normal text-gray-400 text-left truncate"
                scope="row"
              >
                {{ feature.name }}
              </td>

              <td class="py-2 px-6">
                <span class="block text-sm text-gray-500">{{
                  feature.indie
                }}</span>
              </td>
            </tr>
            <tr v-for="feature of featureSetFlags" :key="feature.name">
              <td
                class="py-2 px-6 text-sm font-normal text-gray-400 text-left truncate"
                scope="row"
              >
                {{ feature.name }}
              </td>

              <td class="py-2 px-6">
                <span class="block text-sm text-gray-500">
                  <Check :value="feature.indie" />
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </figure>
      <figure class="rounded-lg py-8 sm:py-14 sm:px-6 lg:px-8">
        <table class="w-full h-px table-fixed">
          <!-- Table Header -->
          <thead>
            <tr>
              <th
                class="pb-4 px-6 text-sm font-medium text-gray-300 text-left"
                scope="col"
              >
                <span class="sr-only">Feature by</span>
                <span>Plan</span>
              </th>

              <th
                class="pb-4 px-6 text-lg leading-6 font-normal text-gray-300 text-left"
                scope="col"
              >
                Pro
              </th>
            </tr>
          </thead>
          <tbody class="border-t border-gray-600">
            <tr>
              <td
                class="py-8 px-6 text-sm font-normal text-gray-400 text-left align-top"
                scope="row"
              >
                Price
              </td>

              <td class="h-full py-8 px-6 align-top">
                <div class="relative h-full table">
                  <p>
                    <span class="text-4xl font-extrabold text-gray-300"
                      >${{
                        annualPricing
                          ? prices.pro.year.toFixed(2)
                          : prices.pro.month.toFixed(2)
                      }}</span
                    >
                    <span class="text-base font-medium text-gray-400">/mo</span>
                  </p>
                  <p class="mt-4 mb-4 text-sm text-gray-400">
                    Serious storage and serious control.
                  </p>
                </div>
              </td>
            </tr>
            <tr v-for="feature of featureSetQuotas" :key="feature.name">
              <td
                class="py-2 px-6 text-sm font-normal text-gray-400 text-left truncate"
                scope="row"
              >
                {{ feature.name }}
              </td>

              <td class="py-2 px-6">
                <span class="block text-sm text-gray-500">{{
                  feature.pro
                }}</span>
              </td>
            </tr>
            <tr v-for="feature of featureSetFlags" :key="feature.name">
              <td
                class="py-2 px-6 text-sm font-normal text-gray-400 text-left truncate"
                scope="row"
              >
                {{ feature.name }}
              </td>

              <td class="py-2 px-6">
                <span class="block text-sm text-gray-500">
                  <Check :value="feature.pro" />
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </figure>
    </section>
    <!-- Desktop view -->
    <section
      :class="[
        'hidden',
        'sm:block',
        'mt-14',
        'sm:mt-0',
        'mb-8',
        'sm:mb-16',
        ...gutters,
        'lg:max-w-5xl',
        'xl:max-w-6xl'
      ]"
    >
      <figure class="rounded-lg underglow py-8 sm:py-14 sm:px-6 lg:px-8">
        <table class="w-full h-px table-fixed">
          <caption class="sr-only">
            Pricing plan comparison
          </caption>
          <!-- Table Header -->
          <thead>
            <tr>
              <th
                class="pb-4 px-6 text-sm font-medium text-gray-300 text-left"
                scope="col"
              >
                <span class="sr-only">Feature by</span>
                <span>Plans</span>
              </th>

              <th
                class="w-1/4 pb-4 px-6 text-lg leading-6 font-normal text-gray-300 text-left"
                scope="col"
              >
                Basic
              </th>

              <th
                class="w-1/4 pb-4 px-6 text-lg leading-6 font-normal text-gray-300 text-left"
                scope="col"
              >
                Indie
              </th>

              <th
                class="w-1/4 pb-4 px-6 text-lg leading-6 font-normal text-gray-300 text-left"
                scope="col"
              >
                Pro
              </th>
            </tr>
          </thead>
          <tbody class="border-t border-gray-600">
            <tr>
              <td
                class="py-8 px-6 text-sm font-normal text-gray-400 text-left align-top"
                scope="row"
              >
                Price
              </td>

              <td class="h-full py-8 px-6 align-top">
                <div class="relative h-full table">
                  <p>
                    <span
                      class="text-2xl lg:text-4xl font-extrabold text-gray-300"
                      >$0</span
                    >
                    <span class="text-base font-medium text-gray-400">/mo</span>
                  </p>
                  <p class="mt-4 mb-4 text-sm text-gray-400">
                    Tossing a couple tracks around? No need to pay!
                  </p>
                </div>
              </td>

              <td class="h-full py-8 px-6 align-top">
                <div class="relative h-full table">
                  <p>
                    <span
                      class="text-2xl lg:text-4xl font-extrabold text-gray-300"
                      >${{
                        annualPricing
                          ? prices.indie.year.toFixed(2)
                          : prices.indie.month.toFixed(2)
                      }}</span
                    >
                    <span class="text-base font-medium text-gray-400">/mo</span>
                  </p>
                  <p class="mt-4 mb-4 text-sm text-gray-400">
                    A little more power behind your workflow.
                  </p>
                </div>
              </td>

              <td class="h-full py-8 px-6 align-top">
                <div class="relative h-full table">
                  <p>
                    <span
                      class="text-2xl lg:text-4xl font-extrabold text-gray-300"
                      >${{
                        annualPricing
                          ? prices.pro.year.toFixed(2)
                          : prices.pro.month.toFixed(2)
                      }}</span
                    >
                    <span class="text-base font-medium text-gray-400">/mo</span>
                  </p>
                  <p class="mt-4 mb-4 text-sm text-gray-400">
                    Serious storage and serious control.
                  </p>
                </div>
              </td>
            </tr>
            <!-- Quota based features -->
            <tr v-for="feature of featureSetQuotas" :key="feature.name">
              <td
                class="py-2 px-6 text-sm font-normal text-gray-400 text-left truncate"
                scope="row"
              >
                {{ feature.name }}
              </td>

              <td class="py-2 px-6">
                <span class="block text-sm text-gray-500">{{
                  feature.basic
                }}</span>
              </td>

              <td class="py-2 px-6">
                <span class="block text-sm text-gray-500">{{
                  feature.indie
                }}</span>
              </td>

              <td class="py-2 px-6">
                <span class="block text-sm text-gray-500">{{
                  feature.pro
                }}</span>
              </td>
            </tr>
            <!-- Flag based features -->
            <tr v-for="feature of featureSetFlags" :key="feature.name">
              <td
                class="py-2 px-6 text-sm font-normal text-gray-400 text-left truncate"
                scope="row"
              >
                {{ feature.name }}
              </td>

              <td class="py-2 px-6">
                <span class="block text-sm">
                  <Check :value="feature.basic" />
                </span>
              </td>

              <td class="py-2 px-6">
                <span class="block text-sm">
                  <Check :value="feature.indie" />
                </span>
              </td>

              <td class="py-2 px-6">
                <span class="block text-sm">
                  <Check :value="feature.pro" />
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <p class="px-6 pt-6 text-sm font-normal text-gray-600 text-left">
          * Feature coming soon
        </p>
      </figure>
    </section>

    <section
      :class="[
        'mt-14',
        'sm:mt-0',
        'text-left',
        'mb-8',
        'sm:mb-16',
        ...gutters,
        'xl:max-w-6xl',
        'lg:max-w-5xl'
      ]"
    >
      <div
        class="mb-10 mt-14 rounded-lg border border-samplyYellow px-6 py-8 sm:p-10 lg:flex lg:items-center"
      >
        <div class="flex-1">
          <div>
            <h3
              class="inline-flex px-4 py-1 rounded-lg text-md font-semibold tracking-wide border border-samplyYellow text-samplyYellow"
            >
              Enterprise
            </h3>
          </div>
          <div class="mt-4 text-md text-samplyYellow text-opacity-60">
            Don't see what you're looking for? Create a plan that's custom to
            the way your team likes to work.
          </div>
        </div>
        <div class="mt-6 rounded-md shadow lg:mt-0 lg:ml-10 lg:flex-shrink-0">
          <a
            :href="mailto"
            class="px-4 py-2 inline-flex items-center rounded-md text-black bg-samplyYellow hover:bg-opacity-10 hover:bg-white hover:bg-opacity-80"
          >
            Let's chat
          </a>
        </div>
      </div>
    </section>
  </main>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import firebase from "firebase/app";
import Check from "./Check.vue";
import features from "./feature-matrix";

export default defineComponent({
  name: "Pricing",
  setup() {
    // UI
    const gutters = [
      "mx-auto",
      "xl:max-w-5xl",
      "lg:max-w-4xl",
      "px-4",
      "sm:px-8",
      "lg:px-10"
    ];

    const annualPricing = ref(true);
    const prices = ref({
      indie: { month: 0, year: 0 },
      pro: { month: 1, year: 1 }
    });

    // Get price data
    const db = firebase.firestore();
    db.collection("stripe_products")
      .where("active", "==", true)
      .get()
      .then(querySnapshot => {
        querySnapshot.forEach(productDoc => {
          const product = productDoc.data();
          const tier = product.role as "indie" | "pro";
          productDoc.ref
            .collection("prices")
            .where("active", "==", true)
            .get()
            .then(snap => {
              for (const priceDoc of snap.docs) {
                const price = priceDoc.data();
                const amount = price.unit_amount;
                const interval = price.interval as "month" | "year";
                prices.value[tier][interval] =
                  amount / 100 / (interval === "year" ? 12 : 1);
              }
            });
        });
      });
    return {
      gutters,
      annualPricing,
      prices,
      featureSetQuotas: features.featuresQuotas,
      featureSetFlags: features.featuresFlags,
      mailto: "mailto:team@samplyaudio.com?subject=Samply%20Pro%20Inquiry"
    };
  },
  components: {
    Check
  }
});
</script>

<style scoped>
.underglow {
  box-shadow: 0 0 1.5rem 0 rgb(14, 129, 223);
}
</style>
